import React, { useRef, useState } from 'react';
import { Button, Field, Input, MessageBar, MessageBarBody, Select, Spinner, Text, Textarea } from '@fluentui/react-components';
import { ArrowRightRegular, EyeRegular, EyeOffRegular, LockClosedRegular } from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import type { Connection, Connector } from '../../api/types';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { validatePostgreSQLFields, type PostgreSQLConnectionFields } from './postgreSQLValidation';
import { postgreSQLTLSConfig, type PostgreSQLTLSMode } from './postgreSQLConfig';
import './PostgreSQLSetup.css';

interface Props {
  onComplete: (connectionId: string) => void;
  connection?: Connection;
  onSaved?: (connection: Connection) => void;
  onCancel?: () => void;
  connector?: Connector;
}

const PostgreSQLSetup: React.FC<Props> = ({ onComplete, connection, onSaved, onCancel }) => {
  const config = connection?.metadataConfig || {};
  const isEditing = !!connection;
  const [name, setName] = useState(connection?.name || '');
  const [host, setHost] = useState(typeof config.host === 'string' ? config.host : '');
  const [port, setPort] = useState(String(config.port ?? 5432));
  const [database, setDatabase] = useState(typeof config.database === 'string' ? config.database : '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [schemas, setSchemas] = useState(Array.isArray(config.schemas) ? config.schemas.join('\n') : '');
  const [ca, setCa] = useState(typeof config.ca === 'string' ? config.ca : '');
  const [sslmode, setSSLMode] = useState<PostgreSQLTLSMode>(config.sslmode === 'disable' ? 'disable' : 'verify-full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const formRef = useRef<HTMLFormElement>(null);
  const retainCredentials = isEditing && connection.hasCredentials === true;
  const errors = validatePostgreSQLFields({ host, port, database, username, password }, retainCredentials);
  const validation = (name: keyof PostgreSQLConnectionFields) => {
    const message = submitted || touched.has(name) ? errors[name] : undefined;
    return { validationMessage: message, validationState: message ? 'error' as const : 'none' as const };
  };

  const connect = async () => {
    if (loading) return;
    setSubmitted(true);
    if (isEditing && !name.trim()) {
      setError('Enter a connection name.');
      return;
    }
    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid) {
      formRef.current?.querySelector<HTMLInputElement>(`[name="${firstInvalid}"]`)?.focus();
      return;
    }
    setLoading(true);
    setShowPassword(false);
    setError('');
    let connectionId: string | undefined;
    try {
      const metadataConfig = {
        host: host.trim(), port: Number(port), database: database.trim(),
        ...postgreSQLTLSConfig(sslmode, ca),
        // Explicitly clear an old private CA when removed or TLS is disabled.
        ...(isEditing ? { ca: sslmode === 'verify-full' ? ca.trim() : '' } : {}),
        schemas: schemas.split('\n').map(s => s.trim()).filter(Boolean),
      };
      if (connection) {
        const saved = await connectionsService.updateConnection(connection.id, {
          name: name.trim(), metadataConfig,
          credentials: {
            ...(username.trim() ? { username: username.trim() } : {}),
            ...(password ? { password } : {}),
          },
        });
        setPassword('');
        onSaved?.(saved);
        return;
      }
      const created = await connectionsService.createConnection({
        connectorId: 'postgresql', name: `${database.trim()} (PostgreSQL)`,
        credentials: { username: username.trim(), password },
        metadataConfig,
      });
      connectionId = created.id;
      const result = await connectionsService.testConnection(created.id);
      if (!result.success) throw new Error(result.message || 'PostgreSQL connection failed');
      setPassword('');
      onComplete(created.id);
    } catch (reason) {
      let message = reason instanceof Error ? reason.message : 'PostgreSQL connection failed';
      if (connectionId) {
        try { await connectionsService.deleteConnection(connectionId); }
        catch { message += ' The failed draft could not be removed; delete it from Data Sources.'; }
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      ref={formRef}
      className="postgresql-setup"
      aria-labelledby="postgresql-title"
      aria-busy={loading}
      noValidate
      onBlur={e => {
        const name = e.target instanceof HTMLInputElement ? e.target.name : '';
        if (name) setTouched(previous => new Set(previous).add(name));
      }}
      onSubmit={e => { e.preventDefault(); void connect(); }}
    >
      <header className="postgresql-setup-header">
        <div className="postgresql-setup-icon" aria-hidden="true">
          <ConnectorIcon connectorId="postgresql" name="PostgreSQL" size={40} />
        </div>
        <div className="postgresql-setup-heading">
          <Text as="h1" id="postgresql-title" size={600} weight="semibold">{isEditing ? 'Edit PostgreSQL connection' : 'Connect PostgreSQL'}</Text>
          <Text as="p" className="postgresql-setup-muted">{isEditing ? 'Changes are tested before saving. Existing data and table selections are retained; no import is started.' : 'Connect your database, then choose the tables to import.'}</Text>
        </div>
      </header>

      {sslmode === 'verify-full' ? <div className="postgresql-setup-security">
        <LockClosedRegular aria-hidden="true" />
        <Text size={200}>TLS encryption and server identity verification are enabled.</Text>
      </div> : <MessageBar className="postgresql-setup-message" layout="multiline" intent="warning" role="alert"><MessageBarBody className="postgresql-setup-message-body">
        TLS is disabled. Credentials and data may be exposed over an unencrypted connection.
        Use only for local development on a trusted network, never for production.
      </MessageBarBody></MessageBar>}

      <section className="postgresql-setup-section" aria-labelledby="postgresql-server-title">
        <Text as="h2" id="postgresql-server-title" size={400} weight="semibold">Connection details</Text>
        {isEditing && <Field label="Connection name" required>
          <Input name="connectionName" value={name} onChange={(_, d) => setName(d.value)} disabled={loading} />
        </Field>}
        <div className="postgresql-setup-server">
          <Field label="Host" required hint={sslmode === 'verify-full' ? 'Use the hostname on your server’s TLS certificate.' : 'Use the database hostname or IP address.'} {...validation('host')}>
            <Input name="host" required placeholder="db.example.com" spellCheck={false} value={host} onChange={(_, d) => setHost(d.value)} disabled={loading} />
          </Field>
          <Field label="Port" required {...validation('port')}>
            <Input name="port" required type="number" min={1} max={65535} step={1} value={port} onChange={(_, d) => setPort(d.value)} disabled={loading} />
          </Field>
        </div>
        <Field label="Database" required {...validation('database')}>
          <Input name="database" required placeholder="analytics" spellCheck={false} value={database} onChange={(_, d) => setDatabase(d.value)} disabled={loading} />
        </Field>
      </section>

      <section className="postgresql-setup-section" aria-labelledby="postgresql-credentials-title">
        <Text as="h2" id="postgresql-credentials-title" size={400} weight="semibold">Credentials</Text>
        <Text size={200} className="postgresql-setup-muted">Use a dedicated read-only role with schema USAGE and table SELECT permissions.</Text>
        {retainCredentials && <Text size={200}>Leave username and password blank to keep the stored credentials.</Text>}
        <Field label="Username" required={!retainCredentials} {...validation('username')}>
          <Input name="username" required={!retainCredentials} autoComplete="off" placeholder={retainCredentials ? 'Keep stored username' : 'dashboard_reader'} spellCheck={false} value={username} onChange={(_, d) => setUsername(d.value)} disabled={loading} />
        </Field>
        <Field label="Password" required={!retainCredentials} hint="Credentials are encrypted and stored separately from connection settings." {...validation('password')}>
          <Input
            name="password" required={!retainCredentials} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
            value={password} onChange={(_, d) => setPassword(d.value)} disabled={loading}
            contentAfter={
              <Button type="button" appearance="transparent" size="small"
                icon={showPassword ? <EyeOffRegular /> : <EyeRegular />}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={loading} onClick={() => setShowPassword(value => !value)} />
            }
          />
        </Field>
      </section>

      <details className="postgresql-setup-advanced">
        <summary>Advanced settings <span className="postgresql-setup-muted">— optional</span></summary>
        <div className="postgresql-setup-section">
          <Field label="TLS mode" hint="Verified TLS is the default. Connections never automatically fall back to unencrypted mode.">
            <Select value={sslmode} disabled={loading} onChange={(_, data) => {
              if (data.value === 'verify-full' || data.value === 'disable') setSSLMode(data.value);
            }}>
              <option value="verify-full">Verified TLS (recommended)</option>
              <option value="disable">Disabled (insecure)</option>
            </Select>
          </Field>
          <Field label="Schemas" hint="One exact schema name per line. Leave empty to discover all accessible non-system schemas.">
            <Textarea rows={3} placeholder="public" spellCheck={false} value={schemas} onChange={(_, d) => setSchemas(d.value)} disabled={loading} />
          </Field>
          {sslmode === 'verify-full' && <Field label="CA certificate" hint="Paste a PEM certificate if your server uses a private certificate authority. Otherwise, system trust is used.">
            <Textarea rows={4} resize="vertical" spellCheck={false} value={ca} onChange={(_, d) => setCa(d.value)} disabled={loading} />
          </Field>}
        </div>
      </details>

      {error && <MessageBar className="postgresql-setup-message" layout="multiline" intent="error" role="alert"><MessageBarBody className="postgresql-setup-message-body">{error}</MessageBarBody></MessageBar>}
      <footer className="postgresql-setup-footer">
        <Text size={200} className="postgresql-setup-muted">Tables are imported as full snapshots. Incremental sync is not supported.</Text>
        <div className="postgresql-setup-actions">
          <Button type="submit" appearance="primary" disabled={loading}
            icon={loading ? <Spinner size="tiny" /> : <ArrowRightRegular />} iconPosition="after">
            {loading ? 'Testing connection…' : isEditing ? 'Test & save changes' : 'Test connection & continue'}
          </Button>
          {onCancel && <Button type="button" disabled={loading} onClick={onCancel}>Cancel</Button>}
        </div>
        <span className="postgresql-setup-muted" role="status">{loading ? 'Verifying access to your database. This may take a moment.' : ''}</span>
      </footer>
    </form>
  );
};

export default PostgreSQLSetup;